import React, { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import FeatherIcon from '../components/FeatherIcon';
import { formatDate } from '../donor/format';
import {
  fetchVoucherAvailability,
  type VoucherAvailability,
  type VoucherAvailabilityReason,
} from '../voucher/voucherApi';

/**
 * The page a voucher's QR code opens (route: /voucher?code=<code>). It asks the backend whether
 * that voucher can be used and says so plainly: ready to use (with the date it is valid through),
 * expired, already used, cancelled, or not found — or that the check itself could not be made.
 * Without a code it asks for one, for anyone reading the code off the paper instead of scanning.
 *
 * Every answer comes from GET /api/public/vouchers/:code/availability; nothing here is inferred.
 * Not indexed (public/robots.txt): each address is one person's voucher.
 */

/** A slow mobile connection gets this long before the page offers a retry. */
const CHECK_TIMEOUT_MS = 15_000;
const CONTACT_EMAIL = 'info@pawsitivefuture.org';
const INPUT_CLASS = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm';

type Check =
  | { readonly status: 'checking' }
  | { readonly status: 'checked'; readonly result: VoucherAvailability }
  | { readonly status: 'failed' };

interface Outcome {
  readonly icon: string;
  /** Surface + ink from the themed ramps, so the badge reads in light and dark alike. */
  readonly tone: string;
  readonly title: string;
  readonly body: (result: VoucherAvailability) => ReactNode;
}

const OUTCOMES: Record<VoucherAvailabilityReason, Outcome> = {
  AVAILABLE: {
    icon: 'check-circle',
    tone: 'bg-green-50 text-green-700',
    title: 'This voucher is ready to use',
    body: ({ expiresAt }) => (
      <>
        <p className="text-lg font-semibold text-gray-800">Valid through {formatDate(expiresAt ?? '')}</p>
        <p className="mt-3 text-gray-600">Bring this voucher to your veterinary appointment. The clinic will scan it to confirm.</p>
      </>
    ),
  },
  EXPIRED: {
    icon: 'clock',
    tone: 'bg-amber-50 text-amber-700',
    title: 'This voucher has expired',
    body: () => <p className="text-gray-600">It is past its expiry date and can no longer be used for an appointment.</p>,
  },
  USED: {
    icon: 'slash',
    tone: 'bg-gray-100 text-gray-700',
    title: 'This voucher has already been used',
    body: () => <p className="text-gray-600">It has already been presented at a clinic, so it can&apos;t be used again.</p>,
  },
  VOID: {
    icon: 'x-circle',
    tone: 'bg-red-50 text-red-700',
    title: 'This voucher is no longer valid',
    body: () => <p className="text-gray-600">It has been cancelled and can&apos;t be used.</p>,
  },
  NOT_RECOGNISED: {
    icon: 'help-circle',
    tone: 'bg-gray-100 text-gray-700',
    title: "We couldn't find this voucher",
    body: () => (
      <p className="text-gray-600">
        Check that you scanned the QR code on your voucher, or that the code matches the one printed on it.
      </p>
    ),
  },
};

const StatusBadge: React.FC<{ icon: string; tone: string }> = ({ icon, tone }) => (
  <span className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full ${tone}`} aria-hidden="true">
    <FeatherIcon name={icon} size={32} />
  </span>
);

const CodeLine: React.FC<{ code: string }> = ({ code }) => (
  <p className="mt-6 text-xs text-gray-500">
    Voucher code <span className="block mt-1 font-mono text-sm text-gray-700 break-all">{code}</span>
  </p>
);

const HelpLine: React.FC = () => (
  <p className="mt-6 text-sm text-gray-500">
    Think this is a mistake? Contact the organization that gave you this voucher, or email us at{' '}
    <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-primary hover:underline">{CONTACT_EMAIL}</a>.
  </p>
);

const CodeForm: React.FC<{ onSubmit: (code: string) => void }> = ({ onSubmit }) => {
  const [value, setValue] = useState('');
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (value.trim()) onSubmit(value.trim());
  };
  return (
    <>
      <StatusBadge icon="search" tone="bg-blue-50 text-blue-700" />
      <h1 className="text-2xl font-bold text-center">Check a voucher</h1>
      <p className="mt-2 text-center text-gray-600">
        Scan the QR code on your voucher with your phone&apos;s camera, or enter the code printed on it.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-4 text-left">
        <div>
          <label htmlFor="voucher-code" className="block text-sm font-medium text-gray-700 mb-1">Voucher code</label>
          <input
            id="voucher-code"
            className={INPUT_CLASS}
            type="text"
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          className="btn-primary w-full px-6 py-3 rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!value.trim()}
        >
          Check voucher
        </button>
      </form>
    </>
  );
};

const VoucherPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const code = (searchParams.get('code') ?? '').trim();
  const [attempt, setAttempt] = useState(0);
  const [check, setCheck] = useState<Check>({ status: 'checking' });

  useEffect(() => {
    if (!code) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
    setCheck({ status: 'checking' });
    fetchVoucherAvailability(code, controller.signal)
      .then((result) => { if (!cancelled) setCheck({ status: 'checked', result }); })
      .catch(() => { if (!cancelled) setCheck({ status: 'failed' }); })
      .finally(() => clearTimeout(timer));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [code, attempt]);

  let content: ReactNode;
  if (!code) {
    content = <CodeForm onSubmit={(next) => setSearchParams({ code: next })} />;
  } else if (check.status === 'checking') {
    content = (
      <div className="py-6">
        <span
          className="mx-auto mb-5 block h-12 w-12 rounded-full border-4 border-gray-200 border-t-primary motion-safe:animate-spin"
          aria-hidden="true"
        />
        <h1 className="text-xl font-semibold text-gray-800">Checking your voucher…</h1>
      </div>
    );
  } else if (check.status === 'failed') {
    content = (
      <>
        <StatusBadge icon="alert-circle" tone="bg-amber-50 text-amber-700" />
        <h1 className="text-2xl font-bold">We couldn&apos;t check this voucher right now</h1>
        <p className="mt-3 text-gray-600">
          We couldn&apos;t reach our voucher service. Nothing has changed with your voucher — please try again in a moment.
        </p>
        <button
          type="button"
          onClick={() => setAttempt((n) => n + 1)}
          className="btn-primary mt-6 w-full px-6 py-3 rounded-full font-medium"
        >
          Try again
        </button>
        <CodeLine code={code} />
      </>
    );
  } else {
    const outcome = OUTCOMES[check.result.reason];
    content = (
      <>
        <StatusBadge icon={outcome.icon} tone={outcome.tone} />
        <h1 className="text-2xl font-bold">{outcome.title}</h1>
        <div className="mt-3">{outcome.body(check.result)}</div>
        {!check.result.available && <HelpLine />}
        <CodeLine code={code} />
      </>
    );
  }

  return (
    <section className="py-20 px-6">
      <div className="mx-auto max-w-md card p-8 text-center">
        {/* One live region for every state, so the answer is announced when it replaces "Checking". */}
        <div aria-live="polite">{content}</div>
        {code && (
          <Link to="/voucher" className="mt-8 inline-block text-sm font-medium text-gray-500 hover:text-primary">
            Check another voucher
          </Link>
        )}
      </div>
    </section>
  );
};

export default VoucherPage;
